import React, { useRef, useState, useEffect } from 'react';
import { Card, Button } from '../components/common';

/**
 * PoseTracker Test Page - Standard Tracking Endpoint
 * Using: https://app.posetracker.com/pose_tracker/tracking
 */
const PoseTrackerTestPage = () => {
  const iframeRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [metrics, setMetrics] = useState({
    reps: 0,
    exercise: null,
    status: null,
    rawData: null
  });
  const [selectedExercise, setSelectedExercise] = useState('squat');
  const [iframeKey, setIframeKey] = useState(0);

  const API_KEY = 'f1e47496-087e-4338-b009-a4d24fed47e6';

  const exercises = [
    { id: 'squat', name: 'Squats', icon: '🏋️' },
    { id: 'pushup', name: 'Push-ups', icon: '💪' },
    { id: 'jumping_jack', name: 'Jumping Jacks', icon: '🤸' },
    { id: 'plank', name: 'Plank', icon: '🧘' },
    { id: 'lunge', name: 'Lunges', icon: '🦵' },
  ];

  // Listen for messages from PoseTracker
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "https://app.posetracker.com") return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        // Log all messages
        setMessages(prev => [{
          timestamp: new Date().toLocaleTimeString(),
          data: data
        }, ...prev].slice(0, 50));

        // Update metrics based on message type
        if (data.type === 'rep_count' || data.reps !== undefined) {
          setMetrics(prev => ({
            ...prev,
            reps: data.reps || data.count || prev.reps,
            rawData: data
          }));
        }

        if (data.type === 'status' || data.status) {
          setMetrics(prev => ({
            ...prev,
            status: data.status || data.message,
            rawData: data
          }));
        }

        if (data.type === 'exercise') {
          setMetrics(prev => ({
            ...prev,
            exercise: data.exercise,
            rawData: data
          }));
        }

        // Log to console for debugging
        console.log('PoseTracker message:', data);
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleExerciseChange = (exerciseId) => {
    setSelectedExercise(exerciseId);
    setMetrics({ reps: 0, exercise: null, status: null, rawData: null });
    setMessages([]);
    // Force iframe reload by changing key
    setIframeKey(prev => prev + 1);
  };

  const handleReset = () => {
    setMetrics({ reps: 0, exercise: null, status: null, rawData: null });
    setMessages([]);
    setIframeKey(prev => prev + 1);
  };

  const handleSaveSession = () => {
    const sessionData = {
      exercise: selectedExercise,
      finalReps: metrics.reps,
      messages: messages,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(sessionData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `posetracker-${selectedExercise}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    alert(`Session saved! ${metrics.reps} reps recorded`);
  };

  const iframeUrl = `https://app.posetracker.com/pose_tracker/tracking?token=${API_KEY}&exercise=${selectedExercise}&keypoints=true&width=100%&height=100%`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-100 mb-2">
            🎥 PoseTracker Standard Tracking
          </h1>
          <p className="text-slate-400">
            Official tracking endpoint with automatic rep counting
          </p>
        </div>

        {/* Exercise Selection */}
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Select Exercise</h3>
          <div className="flex gap-3 flex-wrap">
            {exercises.map(ex => (
              <button
                key={ex.id}
                onClick={() => handleExerciseChange(ex.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedExercise === ex.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {ex.icon} {ex.name}
              </button>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - PoseTracker iframe */}
          <div className="lg:col-span-2 space-y-6">
            <Card padding="none">
              <div className="relative bg-black rounded-xl overflow-hidden" style={{ minHeight: '480px' }}>
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={iframeUrl}
                  className="w-full aspect-video rounded-xl"
                  allow="camera; microphone"
                  title="PoseTracker"
                />
              </div>
            </Card>

            {/* Controls */}
            <Card>
              <div className="flex gap-3 justify-center flex-wrap">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={handleReset}
                >
                  Reset / Reload
                </Button>
                
                {messages.length > 0 && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleSaveSession}
                  >
                    Save Session JSON
                  </Button>
                )}
              </div>
              <p className="text-center text-slate-500 text-sm mt-3">
                {messages.length > 0 
                  ? `${messages.length} messages received` 
                  : 'Allow camera access to start tracking'}
              </p>
            </Card>

            {/* Message Log */}
            <Card title="📨 PostMessage Log">
              <div className="bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto font-mono text-xs">
                {messages.length === 0 ? (
                  <p className="text-slate-500">Waiting for messages from PoseTracker...</p>
                ) : (
                  messages.map((msg, index) => (
                    <div key={index} className="mb-3 pb-3 border-b border-slate-800">
                      <div className="text-primary-400 mb-1">{msg.timestamp}</div>
                      <pre className="text-slate-300 whitespace-pre-wrap break-all">
                        {JSON.stringify(msg.data, null, 2)}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Right - Metrics */}
          <div className="space-y-4">
            {/* Reps Counter */}
            <Card className="bg-gradient-to-br from-primary-600 to-primary-700 border-primary-500">
              <div className="text-center">
                <p className="text-primary-100 text-sm mb-2">Reps Counted</p>
                <div className="text-6xl font-bold text-white mb-2 animate-pulse">
                  {metrics.reps}
                </div>
                <p className="text-primary-100 text-sm">
                  {exercises.find(e => e.id === selectedExercise)?.name}
                </p>
              </div>
            </Card>

            {/* Status */}
            {metrics.status && (
              <Card className="bg-gradient-to-br from-secondary-600 to-secondary-700 border-secondary-500">
                <div className="text-center">
                  <p className="text-secondary-100 text-sm mb-2">Status</p>
                  <div className="text-lg font-bold text-white">
                    {metrics.status}
                  </div>
                </div>
              </Card>
            )}

            {/* Raw Data */}
            {metrics.rawData && (
              <Card title="📊 Latest Data">
                <div className="bg-slate-900 rounded-lg p-3 max-h-64 overflow-y-auto">
                  <pre className="text-xs text-slate-400 whitespace-pre-wrap break-all">
                    {JSON.stringify(metrics.rawData, null, 2)}
                  </pre>
                </div>
              </Card>
            )}

            {/* API Info */}
            <Card title="ℹ️ Integration Info">
              <div className="space-y-2 text-sm text-slate-400">
                <p><strong className="text-slate-200">Method:</strong> Standard Tracking</p>
                <p><strong className="text-slate-200">Endpoint:</strong> /pose_tracker/tracking</p>
                <p><strong className="text-slate-200">API Key:</strong> {API_KEY.substring(0, 20)}...</p>
                <p><strong className="text-slate-200">Exercise:</strong> {selectedExercise}</p>
                <p><strong className="text-slate-200">Keypoints:</strong> Enabled</p>
              </div>
            </Card>

            {/* Instructions */}
            <Card title="📋 Instructions">
              <div className="space-y-2 text-sm text-slate-400">
                <p>1. Allow camera access</p>
                <p>2. Position yourself in frame</p>
                <p>3. Start exercising</p>
                <p>4. Watch reps count automatically!</p>
                <p className="text-primary-400 mt-3">
                  💡 PoseTracker handles everything - camera, tracking, and rep counting!
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoseTrackerTestPage;
